/**
 * Billing types.
 *
 * These mirror the payloads served by the backend's plan catalog
 * (`src/config/plans.js`) and entitlement service. The catalog itself is never
 * duplicated here — it is always fetched — so a price change ships by editing
 * one file on the backend.
 *
 * `null` consistently means "unlimited" for any limit or cap.
 */

export type PlanId = 'STARTER' | 'GROWTH' | 'SCALE' | 'ENTERPRISE';

export type BillingInterval = 'monthly' | 'annual';

/** Progressively richer AI capability, one step per tier. */
export type AiTier = 'manual' | 'text' | 'voice' | 'custom';

/** How proof-of-delivery images are budgeted on a plan. */
export type PodMode = 'rolling_total' | 'per_driver_monthly' | 'unlimited';

export type SubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'incomplete'
  | 'paused';

export type BlockedReason =
  | 'TRIAL_EXPIRED'
  | 'NO_SUBSCRIPTION'
  | 'SUBSCRIPTION_INACTIVE'
  | null;

export interface PlanFeatures {
  route_planning: boolean;
  route_optimization: boolean;
  route_execution: boolean;
  mobile_app: boolean;
  real_time_tracking: boolean;
  proof_of_delivery: boolean;
  customer_tracking: boolean;
  digital_signatures: boolean;
  whatsapp_manual_chat: boolean;
  whatsapp_ai_assistant: boolean;
  whatsapp_ai_voice: boolean;
  ai_custom_agents: boolean;
  pod_quality_check: boolean;
  analytics: 'basic' | 'advanced' | 'enterprise';
  api_access: boolean;
  webhooks: boolean;
  white_label: boolean;
  priority_support: boolean;
  sla_guarantee: boolean;
  dedicated_account_manager: boolean;
}

export type FeatureKey = keyof PlanFeatures;

export interface Plan {
  id: PlanId;
  name: string;
  order: number;
  tagline: string;
  description: string;
  /** Sales-led pricing: show "Custom" rather than a number. */
  isCustom: boolean;
  /** Whether the plan can be bought through Stripe Checkout. */
  selfServe: boolean;
  recommended: boolean;
  price: {
    monthly: number | null;
    annual: number | null;
    currency: string;
    /** Render the price as a floor ("from $1,999"). */
    startsAt: boolean;
    /** Months billed on the annual plan; the rest are free. */
    monthsChargedAnnually: number;
  };
  limits: {
    drivers: number | null;
    tasksPerMonth: number | null;
    pingIntervalSeconds: number;
    estimatedTelemetryGb: number | null;
  };
  overage: {
    ratePerTask: number | null;
  };
  ai: {
    tier: AiTier;
    messagesPerDriverPerDay: number | null;
  };
  pod: {
    mode: PodMode;
    totalImages: number | null;
    perDriverPerMonth: number | null;
    retentionDays: number | null;
    qualityCheck: boolean;
    coldStorage: boolean;
  };
  features: PlanFeatures;
  highlights: string[];
  competitor: {
    name: string;
    note: string;
  };
}

export interface PlanCatalog {
  plans: Plan[];
  trial: {
    days: number;
    entitlementTier: PlanId;
    taskCap: number;
  };
  annualMonthsCharged: number;
}

export interface TrialState {
  active: boolean;
  startedAt: string | null;
  endsAt: string | null;
  daysRemaining: number;
  converted: boolean;
}

/** What the organization is currently allowed to do. */
export interface Entitlements {
  /** The plan the customer pays for. */
  planId: PlanId;
  planName: string;
  /** The plan actually in force — Enterprise during a trial. */
  effectivePlanId: PlanId;
  effectivePlanName: string;
  interval: BillingInterval;
  status: SubscriptionStatus;
  trial: TrialState;
  limits: Plan['limits'];
  overage: { ratePerTaskCents: number | null; ratePerTask: number | null };
  ai: Plan['ai'];
  pod: Plan['pod'];
  features: PlanFeatures;
  pingIntervalSeconds: number;
  accessBlocked: boolean;
  blockedReason: BlockedReason;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  plan: Plan;
  effectivePlan: Plan;
}

export interface UsageSummary {
  plan: Plan;
  effectivePlanId: PlanId;
  interval: BillingInterval;
  status: SubscriptionStatus;
  trial: TrialState;
  accessBlocked: boolean;
  blockedReason: BlockedReason;
  cancelAtPeriodEnd: boolean;

  period: { start: string | null; end: string | null };

  drivers: {
    used: number;
    limit: number | null;
    remaining: number | null;
    percentUsed: number;
  };

  tasks: {
    used: number;
    limit: number | null;
    remaining: number | null;
    percentUsed: number;
    overage: number;
    overageRate: number;
    overageCost: number;
    /** Always true: dispatch is never frozen at the cap. */
    softCap: boolean;
    consecutiveMonthsOverCap: number;
    shouldPromptUpgrade: boolean;
  };

  ai: {
    tier: AiTier;
    enabled: boolean;
    voiceEnabled: boolean;
    messagesPerDriverPerDay: number | null;
    messagesThisPeriod: number;
  };

  pod: {
    mode: PodMode;
    stored: number | null;
    totalImages: number | null;
    perDriverPerMonth: number | null;
    retentionDays: number | null;
    qualityCheck: boolean;
    coldStorage: boolean;
    uploadedThisPeriod: number;
  };

  telemetry: {
    pingIntervalSeconds: number;
    estimatedGbPerMonth: number | null;
  };

  cost: {
    base: number;
    overage: number;
    estimatedTotal: number;
    currency: string;
  };

  features: PlanFeatures;
}

export interface Invoice {
  id: string;
  number: string | null;
  status: string;
  amountDue: number;
  amountPaid: number;
  currency: string;
  created: string;
  periodStart: string | null;
  periodEnd: string | null;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
}

export interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}

/**
 * The structured 402/403 body the backend returns when a plan limit blocks a
 * request, so the UI can render a precise upgrade prompt.
 */
export interface PlanLimitError {
  code:
    | 'FEATURE_NOT_AVAILABLE'
    | 'TIER_REQUIRED'
    | 'DRIVER_LIMIT_EXCEEDED'
    | 'POD_STORAGE_LIMIT_REACHED'
    | 'AI_DAILY_LIMIT_REACHED'
    | 'AI_NOT_IN_PLAN'
    | 'TRIAL_EXPIRED'
    | 'NO_SUBSCRIPTION'
    | 'SUBSCRIPTION_INACTIVE'
    | 'SUBSCRIPTION_REQUIRED';
  message: string;
  feature?: FeatureKey;
  currentPlan?: PlanId;
  currentPlanName?: string;
  requiredPlan?: PlanId;
  requiredPlanName?: string;
  suggestedPlan?: PlanId;
  limit?: number;
  currentUsage?: number;
  upgradeUrl?: string;
}

export const TIER_ORDER: PlanId[] = ['STARTER', 'GROWTH', 'SCALE', 'ENTERPRISE'];

/** Negative when `a` is the lower tier. */
export const compareTiers = (a: PlanId, b: PlanId): number =>
  TIER_ORDER.indexOf(a) - TIER_ORDER.indexOf(b);

export const isUpgrade = (from: PlanId, to: PlanId): boolean => compareTiers(from, to) < 0;

/** Formats a limit for display, rendering `null` as "Unlimited". */
export const formatLimit = (value: number | null, unlimited = 'Unlimited'): string =>
  value === null ? unlimited : value.toLocaleString();

export const formatMoney = (amount: number, currency = 'usd'): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);

/** What the customer pays each month on a given interval. */
export const monthlyEquivalent = (plan: Plan, interval: BillingInterval): number | null => {
  if (interval === 'annual' && plan.price.annual !== null) return plan.price.annual / 12;
  return plan.price.monthly;
};

/** Percentage saved by paying annually, or 0 when there is no annual price. */
export const annualSavingPercent = (plan: Plan): number => {
  if (plan.price.monthly === null || plan.price.annual === null) return 0;
  const fullYear = plan.price.monthly * 12;
  return Math.round(((fullYear - plan.price.annual) / fullYear) * 100);
};

export const AI_TIER_LABELS: Record<AiTier, string> = {
  manual: 'Manual chat only',
  text: 'Text AI assistant',
  voice: 'Voice and text AI',
  custom: 'Custom AI agents',
};
